import { yupResolver } from '@hookform/resolvers/yup';
import { BookingTypes, OrdersApi, TasksApi } from 'api';
import { confirmBookingPhotographerMutation, getProposalApi, updateBookingTypeMutation } from 'api/bookings/query';

import { bookPhotographerSchema } from 'components/v2/division/form/schema/createOrderSchema';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import QUERIES from 'modules/common/queries';

export const useBookingPhotographer = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('id') ?? '';

  const {
    data: bookingInformation,
    isLoading: isTaskLoading,
    isFetching: isTaskFetching,
    refetch,
  } = useQuery({
    queryKey: [QUERIES.bookingById, bookingId],
    queryFn: () => OrdersApi.getBookingById(bookingId),
    enabled: !!bookingId,
  });
  const { data: taskId } = useQuery({
    queryKey: [QUERIES.bookingTaskByBookingId, bookingId],
    queryFn: () => TasksApi.getBookingTaskByBookingId(bookingId),
    enabled: !!bookingId,
  });

  const { data: proposalData, isLoading: isProposalLoading } = getProposalApi(bookingId);

  const {
    mutateAsync,
    isLoading: isUpdateBookingLoading,
    isSuccess: isUpdateBookingSuccess,
  } = updateBookingTypeMutation();
  const { mutateAsync: mutateBookingPhotographerAsync, isLoading: isBookingPhotographerLoading } =
    confirmBookingPhotographerMutation();

  const form = useForm({
    defaultValues: {
      selectedDate: undefined,
      selectedTime: undefined,
      socialMediaType: '',
      slideShowMediaType: '',
      bookingType: BookingTypes.BookingSecondaryConfirmed,
      inputInstructions: '',
      proposalId: '',
      contactPerson: { label: '', value: '' },
    },
    resolver: yupResolver(bookPhotographerSchema()),
  });

  const { getValues, setValue } = form;

  useEffect(() => {
    const initialDate = dayjs(proposalData?.primary?.arrival);
    const initialTime = initialDate.hour(initialDate.hour()).minute(0);
    setValue('selectedDate', initialDate);
    setValue('selectedTime', initialTime);
    setValue('proposalId', proposalData?.primary?.id ?? '');
  }, [proposalData]);

  const onSubmit = async () => {
    const values = getValues();

    await mutateAsync({
      changeToType: values.bookingType,
      person: '',
      responsibleUser: '',
      bookingId,
      taskId,
    });

    if (values.bookingType === BookingTypes.BookingSecondaryConfirmed) {
      await mutateBookingPhotographerAsync({
        proposalId: values?.proposalId ?? '',
        bookingId,
      });
    }
    refetch();
  };

  return {
    form,
    actions: {
      onSubmit,
    },
    apiStates: {
      bookingInformation,
      proposalData,
    },
    loading: {
      isTaskLoading,
      isUpdateBookingLoading,
      isBookingPhotographerLoading,
      isProposalLoading,
      isUpdateBookingSuccess,
      isTaskFetching,
    },
  };
};
